import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { ACQUISITION_CARD_WIDTH, STEPPER_MAX_WIDTH } from '../../../theme';
import { Divider, Grid, Paper, Typography } from '@material-ui/core';
import SatelliteChooser from '../../components/acquisition/SatelliteChooser';
import AOIChooser from '../../components/euscoast/AOIChooser';
import LandCover from '../../components/euscoast/LandCover';
import PeriodImageChooser from '../../components/acquisition/PeriodImageChooser';
import WorkflowStepper from '../../components/steps/WorkflowStepper';
import StepSummary from '../../components/steps/StepSummary';
import Footer from '../../components/core/Footer';
import { Actions as Auth } from '../../../store/ducks/auth';
import { FIRST, useStepNavigations } from '../../../hooks/useStepNavigations';

/**
 * Module for the EUSCoast process (urban occupation growth along the coast)
 * @module Euscoast
 */
/**
 * @constant MODULE_ACCENT
 * @description Euscoast's identity color. Shared by the page header and the step indicator, so the
 * two read as one module instead of as two unrelated palettes.
 * @memberof module:Euscoast
 */
const MODULE_ACCENT = '#A83232';

const STEPS = [
  {
    label: 'forms.euscoast.1.title',
    content: 'forms.euscoast.1.description',
    component: (props) => <SatelliteChooser {...props} variant="modern" />,
  },
  {
    label: 'forms.euscoast.2.title',
    content: 'forms.euscoast.2.description',
    requires: 'satellite',
    component: AOIChooser,
  },
  {
    label: 'forms.euscoast.3.title',
    content: 'forms.euscoast.3.description',
    requires: 'geometry',
    props: { module: 'euscoast', periodIndex: 0 },
    component: (props) => <PeriodImageChooser {...props} variant="modern" />,
  },
  {
    label: 'forms.euscoast.4.title',
    content: 'forms.euscoast.4.description',
    requires: 'geometry',
    props: { module: 'euscoast', periodIndex: 1 },
    component: (props) => <PeriodImageChooser {...props} variant="modern" />,
  },
  {
    label: 'forms.euscoast.5.title',
    content: 'forms.euscoast.5.description',
    component: LandCover,
  },
];

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexFlow: 'column',
    flexGrow: 1,
  },
  header: {
    backgroundColor: MODULE_ACCENT,
    padding: theme.spacing(4, 0, 12),
  },
  title: {
    color: 'white',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
  },
  content: {
    width: ACQUISITION_CARD_WIDTH,
    marginTop: theme.spacing(-9),
  },
  summary: {
    marginBottom: theme.spacing(1),
  },
  instructions: {
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
    whiteSpace: 'pre-line',
  },
  stepper: {
    maxWidth: STEPPER_MAX_WIDTH,
    padding: theme.spacing(4, 3, 1.5),
  },
}));

/**
 * @function EUSCoastPage
 * @description Main component for the EUSCoast acquisition process.
 * The acquisition process is divided into multiple steps, each represented by a separate component.
 * @param {Object} props - Component properties passed from the router (match, location, history)
 * @see {@link SatelliteChooser} - Component for selecting satellite
 * @see {@link AOIChooser} - Component for defining area of interest (AOI)
 * @see {@link PeriodAndImageEUSCoast} - Component for refining image selection
 * @see {@link LandCover} - Component for land-cover classification
 * **Called by:
 * @see {MainPage} - via the `/euscoast/:step?` Route (imported as EUSCoast).
 * @memberof module:Euscoast
 * @returns {JSX.Element} Complete acquisition page JSX element
 */
const EUSCoastPage = (props) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const classes = useStyles();
  const { match } = props;
  const imagePeriods = useSelector((state) => state.euscoast.imageList);
  const { acquisitionData, stepAttendsRequirements, navigate, navigateTo, canRevisitStep, makeRoutes } =
    useStepNavigations(match, STEPS, `/main/euscoast/result`, 'euscoast');

  useEffect(() => {
    dispatch(Auth.loadClientAuth());
  }, []);

  useEffect(() => {
    dispatch(Auth.begin());
    if (!stepAttendsRequirements()) navigate(FIRST);
  }, [dispatch]);

  const hasStep = match.params.step !== undefined;
  const step = hasStep ? match.params.step - 1 : 0;

  return (
    <div className={classes.wrapper}>
      <Grid container spacing={0} justifyContent="center">
        <Grid item xs={12} className={classes.header}>
          <div>
            <Typography className={classes.title} variant="h4" align="center">
              {t('forms.euscoast.title')}
            </Typography>
          </div>
        </Grid>
        <Grid item className={classes.container}>
          <Paper className={classes.content} elevation={1}>
            <WorkflowStepper
              className={classes.stepper}
              accentColor={MODULE_ACCENT}
              steps={STEPS.map((el, i) => ({ id: String(i), label: t(el.label), disabled: !canRevisitStep(i) }))}
              currentStep={String(step)}
              onStepClick={(i) => canRevisitStep(i) && navigateTo(i)}
            />
            <StepSummary acquisition={acquisitionData} imagePeriods={imagePeriods} className={classes.summary} />
            {STEPS[step].content && (
              <Typography className={classes.instructions} align="center">
                {t(STEPS[step].content)}
              </Typography>
            )}
            <Divider />
            <Switch>{makeRoutes(hasStep, STEPS[step]?.props)}</Switch>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </div>
  );
};

export default EUSCoastPage;
